/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Input, AutoComplete, Spin } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { ArrowRightUniversal, SearchUniversal } from "@duxton-web/icons"
import { storageHelper } from "utils/storage"
import { useTranslations } from "common/utils/i18n"
import { compact } from "lodash"
import { FC } from "react"
import Highlighter from "react-highlight-words"
import { SearchNotFound } from "./SearchNotFound"
import {
  SEARCH_FIELD,
  FILTER_LIMIT,
  SearchCategory,
  SEARCH_LIMIT,
} from "./searchBarConstants"

const loadingSearchStyle = css`
  text-align: center;
  color: "#979797";
`

export interface IMenuStoreListSearch extends FormComponentProps {
  isLoading: boolean
  placeholder: string
  listSource: any
  disabledMode?: boolean
  onSearch?(value: string): void
  onSelect?(
    rank: number,
    merchantID: string,
    merchantName: string,
    searchCategory: string,
  ): void
  onClick?(): void
}

export const MenuStoreListSearchComponent: FC<IMenuStoreListSearch> = ({
  isLoading,
  placeholder,
  listSource,
  disabledMode,
  onSearch,
  onSelect,
  onClick,
  form,
}: IMenuStoreListSearch) => {
  const {
    getFieldDecorator,
    getFieldValue,
    resetFields,
    setFieldsValue,
    getFieldError,
  } = form

  const [
    recentlySearchedText,
    searchingStoreText,
    characterExceedText,
  ] = useTranslations([
    "menu.overview.recentlySearched",
    "common.searchingStore",
    "common.characterExceed",
  ])

  const searchValue = (getFieldValue(SEARCH_FIELD) || "").trim()

  const searchError = getFieldError(SEARCH_FIELD)?.[0]

  const searchLimitValidator = (rule: any, value: string, callback: any) => {
    if (value.length > SEARCH_LIMIT) {
      callback(rule)
    } else {
      callback()
    }
  }

  const handleClearInput = () => {
    resetFields()
    onSearch?.("")
  }

  const handleSelect = (_: string, option: any) => {
    const { rank, record, searchCategory } = option.props
    // small hack
    setTimeout(() => {
      setFieldsValue({
        [SEARCH_FIELD]: record?.merchantName,
      })
    }, 0)
    onSelect?.(rank, record?.merchantID, record?.merchantName, searchCategory)

    storageHelper.setStoreSearchHistory({
      merchantID: record?.merchantID,
      merchantName: record?.merchantName,
    })
  }

  const renderOption = (record: any, rank: number, searchCategory: string) => {
    return {
      key: `${searchCategory}_${record?.merchantID}`,
      value: record?.merchantID,
      record,
      rank,
      searchCategory,
      label: (
        <div
          css={css`
            font-size: 14px;
            &:not(:hover) {
              .edit_hover_class {
                visibility: hidden;
              }
            }
          `}
        >
          <Highlighter
            highlightTag="span"
            highlightStyle={{
              fontWeight: 500,
            }}
            autoEscape
            searchWords={[searchValue]}
            textToHighlight={record?.merchantName ?? ""}
          />
          <span className="edit_hover_class">
            <ArrowRightUniversal style={{ color: "#0091FF" }} />
          </span>
        </div>
      ),
    }
  }

  const filteredSource = searchValue
    ? listSource
        .filter((val: any) =>
          val?.merchantName?.toLowerCase().includes(searchValue?.toLowerCase()),
        )
        .slice(0, FILTER_LIMIT)
    : []

  const recentSearch = storageHelper.getStoreSearchHistory()

  const renderEmptySearch = () => {
    if (searchError)
      return (
        <div css={loadingSearchStyle}>
          <span>
            {characterExceedText
              .replace("<current>", searchValue.length)
              .replace("<limit>", `${SEARCH_LIMIT}`)}
          </span>
        </div>
      )

    if (isLoading)
      return (
        <div css={loadingSearchStyle}>
          <span style={{ marginRight: 10 }}>{searchingStoreText}</span>
          <Spin size="small" data-testid="loading-icon" />
        </div>
      )
    return <SearchNotFound search={searchValue} />
  }
  const dataSource = compact([
    // Not found placeholder
    !filteredSource?.length &&
      searchValue && {
        key: "not found",
        value: "not found",
        label: renderEmptySearch(),
        disabled: true,
      },
    // Filtered data
    ...filteredSource.map((val: any, idx: number) =>
      renderOption(val, idx + 1, SearchCategory.results),
    ),
    // Recent data
    recentSearch?.length && {
      key: SearchCategory.recent,
      label: (
        <span
          css={css`
            font-weight: 500;
            font-size: 12px;
            line-height: 14px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          `}
        >
          {recentlySearchedText}
        </span>
      ),
      options: recentSearch.map((rec: any, idx: number) =>
        renderOption(
          rec,
          filteredSource.length + 1 + idx,
          SearchCategory.recent,
        ),
      ),
    },
  ])

  const handleSearch = (value: string) => {
    if (value.length > SEARCH_LIMIT) return
    onSearch?.(value)
  }

  return (
    <div>
      {getFieldDecorator(SEARCH_FIELD, {
        initialValue: "",
        rules: [
          {
            validator: searchLimitValidator,
            message: characterExceedText,
          },
        ],
      })(
        <AutoComplete
          data-testid="auto-complete"
          css={css`
            width: 360px;
          `}
          options={dataSource}
          onSelect={handleSelect}
          onSearch={handleSearch}
          disabled={disabledMode}
          allowClear
          onClear={handleClearInput}
        >
          <Input
            data-testid="input"
            placeholder={placeholder}
            prefix={<SearchUniversal />}
            autoComplete="off"
            onClick={() => onClick?.()}
          />
        </AutoComplete>,
      )}
    </div>
  )
}
export const MenuStoreListSearch = Form.create<IMenuStoreListSearch>({})(
  MenuStoreListSearchComponent,
)
